import {
    DemoRosterResponse,
    DemoRosterSaveResponse,
    DemoRosterState,
    DemoUserRosterName,
    DemoUsersState
} from "@/admin/types";
import {
    FetchState,
} from "@/shared/types";
import * as drivewealthService from "@/shared/api/drivewealthService";
import * as storeHelper from "@/shared/storeHelper";
import { defineStore } from "pinia";
import _ from "lodash";
import { useNotifierStore } from "@/shared/store/notifier";

const vueSet = storeHelper.vueSet;

function emptyRosterState (): DemoRosterState {
    return {
        roster: [],
        fetchState: FetchState.Uninitialized
    };
}

export const initialState: DemoUsersState = {
    rostersFetchState: FetchState.Uninitialized,
    outOfDateFetchState: FetchState.Uninitialized,
    areRostersOutOfDate: null,
    openBrokerage: emptyRosterState(),
    riaManaged: emptyRosterState(),
    emailsNotFound: [],
};

export const useDemoUsersStore = defineStore("demoUsers", {
    state: (): DemoUsersState => (_.cloneDeep(initialState)),
    getters: {
        areRostersLoaded: state => state.rostersFetchState === FetchState.Loaded,
        areRostersOutOfDateLoaded: state => state.outOfDateFetchState === FetchState.Loaded,
    },
    actions: {
        setAreRostersOutOfDateLoading () {
            this.outOfDateFetchState = FetchState.Loading;
        },
        setAreRostersOutOfDate (areRostersOutOfDate: boolean | null) {
            this.areRostersOutOfDate = areRostersOutOfDate;
            this.outOfDateFetchState = FetchState.Loaded;
        },

        setRostersLoading () {
            this.rostersFetchState = FetchState.Loading;
        },
        setRosters (response: DemoRosterResponse) {
            vueSet(this.openBrokerage, "roster", response.openBrokerage);
            this.openBrokerage.fetchState = FetchState.Loaded;
            vueSet(this.riaManaged, "roster", response.riaManaged);
            this.riaManaged.fetchState = FetchState.Loaded;
            this.rostersFetchState = FetchState.Loaded;
        },

        setRosterLoading (rosterName) {
            const roster = this[rosterName];
            roster.fetchState = FetchState.Loading;
        },
        setRoster ({ rosterName, response }: { rosterName: DemoUserRosterName, response: DemoRosterSaveResponse }) {
            const roster = this[rosterName];
            vueSet(roster, "roster", response.roster);
            vueSet(this, "emailsNotFound", this.emailsNotFound.concat(response.emailsNotFound));
            roster.fetchState = FetchState.Loaded;
        },
        loadAreRostersOutOfDate () {
            if (this.outOfDateFetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }

            this.setAreRostersOutOfDateLoading();
            return drivewealthService.getDemoMockAreRostersOutOfDate()
                .then(response => {
                    this.setAreRostersOutOfDate(response.data);
                })
                .catch(error => {
                    const msg =
                        "Failed to load if the rosters are out of date, emails are not guranteed to still be present in " +
                        "the Demo environment.";
                    const notifierStore = useNotifierStore();
                    notifierStore.setWarning(msg);
                    return storeHelper.handleError({ error, rethrow: true });
                });
        },

        loadRosters () {
            if (this.rostersFetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }

            this.setRostersLoading();
            return drivewealthService.getDemoMockRosters()
                .then(response => {
                    this.setRosters(response.data);
                })
                .catch(error => {
                    const msg = "Failed to load the rosters for DriveWealth demo users' account-management-type.";
                    const notifierStore = useNotifierStore();
                    notifierStore.setWarning(msg);
                    return storeHelper.handleError({ error, rethrow: true });
                });
        },

        putRoster ({ rosterName, emails }: { rosterName: DemoUserRosterName, emails: string[] }) {
            if (this[rosterName].fetchState === FetchState.Loading) {
                return;
            }

            this.setRosterLoading(rosterName);
            const apiCall = rosterName === "openBrokerage"
                ? drivewealthService.putDemoMockOpenBrokerageRoster
                : drivewealthService.putDemoMockRiaManagedRoster;

            return apiCall({ emails })
                .then(response => {
                    this.setRoster({ rosterName, response: response.data });
                })
                .catch(error => {
                    const accountMgmtType = rosterName === "openBrokerage" ? "Open-Brokerage" : "RIA-Managed";
                    const msg = `Failed to save update to ${accountMgmtType} roster of Demo Users.`;
                    const notifierStore = useNotifierStore();
                    notifierStore.setWarning(msg);
                    return storeHelper.handleError({ error, rethrow: true });
                });
        },

        clearRosters () {
            return drivewealthService.putDemoMockClearRosters()
                .then(_ => {
                    const emptyResponse: DemoRosterResponse = {
                        openBrokerage: initialState.openBrokerage.roster,
                        riaManaged: initialState.riaManaged.roster
                    };

                    this.setRosters(emptyResponse);
                });
        }
    }
});